import { sourceInputAllowed } from '@/simulation/native/authority';
import { pixelControl } from '@/utils/tilePixels';
import { colors } from '@/styles';
import { Signal } from '@/models/data/Signal';
import { TileType } from '@/models/TileTypes';
import {
  defaultTileGraphics,
  connectionGraphics,
} from '@/models/tiles/DefaultTileGraphics';
import {
  CreateActionsFn,
  CreateDefaultStateFn,
  TileDefinition,
  TileGraphicsDefinition,
} from '@/models/tiles/TileDefinition';
import { Side, TileSideIOType } from '@/schemas/tiles';
import { standardBuildAndConnect } from '@/models/logic/utils';
import { LogicGraph } from '../logic/LogicGraph';
import { TileInstance } from './TileInstance';
import { LogicNodeInstance } from '../logic/LogicNodeInstance';
import { LeverNodeState } from '../logic/nodes';
import { distinct } from '@/utils/object';

export interface LeverTileState {
  toggleState: Signal;
}

export class LeverTile implements TileDefinition<LeverTileState> {
  graphics = leverTileGraphicsDefinition;
  type = TileType.Lever;

  name = 'Lever';
  canRotate = false;

  createDefaultState: CreateDefaultStateFn<LeverTileState> = (_) => ({
    toggleState: Signal.LOW,
  });

  buildAndConnectNodes = (data: any) => standardBuildAndConnect('LEVER', data, this);

  createActions: CreateActionsFn<LeverTileState> = ({ instance, eventBus }) => ({
    toggle() {
      if (!sourceInputAllowed(instance.context)) return;
      instance.state.toggleState =
        instance.state.toggleState === Signal.HIGH ? Signal.LOW : Signal.HIGH;
      if (!instance.logicNodeIds) return;
      const nodeIds = distinct(Object.values(instance.logicNodeIds));
      for (const nodeId of nodeIds) {
        eventBus!.trigger(nodeId, 'Lever Flipped');
      }
    },
  });

  syncStateToNodes? = (data: {
    instance: TileInstance<LeverTileState>;
    graph: LogicGraph;
  }) => {
    const { instance, graph } = data;

    if (!instance.logicNodeIds) return;

    const nodeIds = distinct(Object.values(instance.logicNodeIds));
    for (const nodeId of nodeIds) {
      const node = graph.nodes.get(nodeId) as LogicNodeInstance<LeverNodeState>;

      if (!node) continue;

      node.state.isOn = instance.state.toggleState === Signal.HIGH;
    }
  };

  ioSides = {
    [Side.TOP]: { type: TileSideIOType.OUTPUT, forceConnection: false },
    [Side.RIGHT]: { type: TileSideIOType.OUTPUT, forceConnection: false },
    [Side.BOTTOM]: { type: TileSideIOType.OUTPUT, forceConnection: false },
    [Side.LEFT]: { type: TileSideIOType.OUTPUT, forceConnection: false },
  };
}

export const leverTileGraphicsDefinition: TileGraphicsDefinition<LeverTileState> = {
  type: TileType.Lever,
  draw: function (g, { state, meta, connections, context }): void {
    defaultTileGraphics(g);

    const ioSides = {
      [Side.TOP]: { type: TileSideIOType.OUTPUT, forceConnection: false },
      [Side.RIGHT]: { type: TileSideIOType.OUTPUT, forceConnection: false },
      [Side.BOTTOM]: { type: TileSideIOType.OUTPUT, forceConnection: false },
      [Side.LEFT]: { type: TileSideIOType.OUTPUT, forceConnection: false },
    };

    connectionGraphics(
      g,
      colors.signalColor,
      colors.noSignalColor,
      meta,
      connections,
      ioSides,
      context,
    );

    const high = state.toggleState === Signal.HIGH;
    pixelControl(g, high ? colors.signalColor : colors.noSignalColor, false, high);
  },
};
